import React, {createContext, Dispatch, PropsWithChildren, useContext, useEffect, useMemo, useReducer} from "react";
import {
  IAvailableFilter,
  IItemContent,
  IItemSearchResponse,
  ProductList,
  ProductListFilterItemRangeValue,
  ProductListFilterItemType,
  ProductListFilterItemValueType,
  ProductListOrder,
  ProductListOrderBy,
} from "@connox/typescript-library";
import window from "../../lib/WindowExtended";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import {Tracking} from "../../../tracking/tracking";
import {getABTestVariant} from "../../../helpers/getABTestVariant";
import {mapUrlParamsToFilterState} from "./helpers/mapUrlParamsToFilterState";
import {buildRequestString} from "./helpers/buildRequestString";
import {reducer} from "./reducer";
import {ProductListAction} from "./actions";
import {convertToFilterTypes} from "./helpers/convertToFilterTypes";
import {getShopId} from "../../lib/getShopId";
import {useMediaQuery} from "react-responsive";
import {ProductListProps} from "../productList/ProductList";
import {useNonInitialEffect} from "../../../helpers/useNonInitialEffect";
import {getItemsWithPercentDiscount} from "./helpers/percentDiscountHelper";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";

export type ProductListState = {
  instance?: ProductList;
  offset: number;
  limit: number;
  orderBy?: ProductListOrderBy;
  order?: ProductListOrder;
  loading: boolean;
  items: IItemContent[];
  /* All filters that are engaged */
  filters: {[k: string]: ProductListFilterItemValueType};
  userId?: string;
  total: number;
  isLastPage: boolean;
  showFilters: boolean;
  trigger?: boolean;
  /* Map of range filters, that are engaged */
  rangeFilterMap?: Map<string, ProductListFilterItemRangeValue>;
  isOffsetChanged: boolean;
  filtersToShow: string[];
  filtersToHide: string[];
  defaultFilter?: string;
};

export const initialState: ProductListState = {
  offset: 0,
  limit: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
  loading: false,
  items: [],
  filters: {},
  total: 0,
  isLastPage: true,
  showFilters: false,
  rangeFilterMap: new Map(),
  isOffsetChanged: false,
  filtersToShow: [],
  filtersToHide: [],
  defaultFilter: undefined,
};

interface IProductListContext {
  state: ProductListState;
  dispatch: Dispatch<ProductListAction>;
}

export const ProductListContext = createContext<IProductListContext>({
  state: initialState,
  dispatch: () => initialState,
});

export function useProductListContext(): IProductListContext {
  return useContext<IProductListContext>(ProductListContext);
}

export const isSearchPage = document.body.id === "C100013";

const storageIdentifier = isSearchPage ? "searchlist" : "productlist";
const browserStorage = new BrowserStorage<IItemSearchResponse>({
  type: "sessionStorage",
  dataIdentifier: storageIdentifier,
  toBeCompressed: true,
  hoursToCache: 1,
});

const ProductListProvider: React.FC<PropsWithChildren<ProductListProps>> = ({
  children,
  shopId,
  categoryId,
  customerGroupId,
  customerId,
  currency = "USD",
  visitorId,
  filtersToHide,
  filtersToShow,
  defaultFilter,
}) => {
  const isMobile = useMediaQuery({query: "(max-width: 940px)"});

  // 24 products are displayed on mobile devices and 48 on desktop
  const productListLimitNumber = isMobile ? 24 : 48;
  ProductList.PRODUCT_LIST_DEFAULT_LIMIT = productListLimitNumber;

  const firstState: ProductListState = useMemo((): ProductListState => {
    const stateByUrlParams = mapUrlParamsToFilterState(initialState).state;
    // Only for DE: determines what test case is used, depending on visitor id.
    // Also to keep in mind: the last sort option in `ProductSortInput.tsx`
    const isInnofindDefaultSort = getShopId() === 100001 && getABTestVariant(visitorId) === "A";
    // Default order by newest products for design-neuheiten.html
    const isNewProductsSite = document.body.id === "C104719";

    // Track the according sort option to exponea
    Tracking.sendABTestSeenEvent(isInnofindDefaultSort ? "innofind" : "default");

    return {
      ...stateByUrlParams,
      instance: new ProductList(
        shopId,
        categoryId,
        currency,
        undefined,
        customerId ?? undefined,
        customerGroupId ?? undefined,
      ),
      ...(isInnofindDefaultSort && !stateByUrlParams.orderBy && !isNewProductsSite
        ? {orderBy: "innofind" as ProductListOrderBy, userId: visitorId, order: "asc" as ProductListOrder}
        : {}),
      ...(isNewProductsSite && !stateByUrlParams.orderBy
        ? {orderBy: "newest" as ProductListOrderBy, order: "desc" as ProductListOrder}
        : {}),
      filtersToShow,
      filtersToHide,
      defaultFilter,
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, firstState);

  useNonInitialEffect(() => {
    dispatch({
      type: "RESET",
    });
  }, [currency, customerId, customerGroupId]);

  const cleanStorageByCategory = (params: {
    categoryId?: number;
    customerId?: number;
    customerGroupId?: number;
  }): void => {
    const relativeKeys =
      sessionStorage && Object.keys(sessionStorage).filter((key) => key.startsWith(storageIdentifier));
    if (!relativeKeys) {
      return;
    }

    const requestParams = new URLSearchParams(relativeKeys[0]?.substring(storageIdentifier.length + 1));
    if (
      Number(requestParams.get("categoryId")) !== params.categoryId ||
      Number(requestParams.get("customerId")) !== params.customerId ||
      Number(requestParams.get("customerGroupId")) !== params.customerGroupId
    ) {
      relativeKeys.forEach((key) => {
        browserStorage.remove(key);
      });
    }
  };

  useEffect(() => {
    const {instance} = state;
    if (!instance) {
      return;
    }
    const onPopState = () => {
      const {state: mappedState, filters: mappedFilters} = mapUrlParamsToFilterState(state);
      const {limit, order, orderBy} = mappedState;
      dispatch({type: "SET_SORT", payload: {orderBy, order}});
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: convertToFilterTypes(mappedFilters, instance.filter.filterItems)},
      });
      dispatch({type: "TRIGGER"});
      dispatch({type: "SET_LIMIT", payload: limit});
    };

    if (state.instance) {
      window.addEventListener("popstate", onPopState);
      cleanStorageByCategory({...state.instance.requestParams});
    }

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [state.instance]);

  const searchListFetch = async () => {
    const {instance} = state;
    if (!instance) {
      return Promise.reject();
    }
    const reqParam = new URLSearchParams(instance.requestParams);
    Object.keys(instance.requestParams).forEach((rqp) => {
      if (!instance.requestParams[rqp]) {
        reqParam.delete(rqp);
      }
    });
    reqParam.delete("categoryId");

    const searchQuery = new URLSearchParams(window.location.search).get("q");
    if (searchQuery) {
      reqParam.append("q", searchQuery);
    }
    if (UrlSearchParamsHelper.promotions?.id) {
      reqParam.append("promotionId", `${UrlSearchParamsHelper.promotions.id}`);
    }
    const getUrl = `${__API_URL__}/${shopId}/search?${reqParam.toString()}`;

    return await fetch(getUrl)
      .then(
        (resp) =>
          resp.json() as Promise<{
            success: boolean;
            result: IItemSearchResponse;
          }>,
      )
      .then((response) => {
        const {result} = response;
        result.availableFilters = result.availableFilters.filter((af) => !!af.values);
        instance.filter.applyFilterResult(result.availableFilters);
        instance.items = result.itemContent;
        instance.total = result.totalSearchHits;

        return result;
      });
  };

  const initialRangeFilters = (filtersFromResponse: IAvailableFilter[]) => {
    const {instance} = state;

    if (!instance) {
      return;
    }
    /* 'field' key string array of any set range filter */
    const anySetRangeFilters = Array.from(instance.filter.filterItems).reduce((acc: string[], [key, keyValue]) => {
      if (keyValue.type === ProductListFilterItemType.range && state.filters[key] !== undefined) {
        acc.push(key);
      }

      return acc;
    }, []);

    if (anySetRangeFilters.length) {
      return;
    }

    filtersFromResponse.forEach((filter) => {
      if (filter.type === ProductListFilterItemType.range)
        dispatch({
          type: "SET_RANGE_FILTERS_VALUES",
          payload: {
            field: filter.field,
            value: {
              min: Math.floor(Number(filter.minValue ?? 0)),
              max: Math.ceil(Number(filter.maxValue ?? 0)),
            },
          },
        });
    });
  };

  const handleResponse = (data: IItemSearchResponse, toAdd = false) => {
    const {instance} = state;
    if (!instance) {
      return;
    }

    initialRangeFilters(data.availableFilters);

    // check if first load and if search param filters contains state filters
    const {filters: paramsFilters} = mapUrlParamsToFilterState(state);

    // Mostly relevant after the first call. Set defaultFilters as soon as available filters are known
    // Will be hidden from the browser url in `updateProductListRequestParams.ts`
    // Will be hidden from Filter View in `ProductListFilter.filterItemArray()`
    // and from filter overview bar in `SelectedFilterOverview.filtersEntries` const
    if (defaultFilter) {
      const [key, value] = defaultFilter.split("=");
      paramsFilters[key] = value;
    }

    if (Object.keys(paramsFilters).some((filterKey) => !state.filters[filterKey])) {
      dispatch({
        type: "SET_FILTERS",
        payload: {filters: convertToFilterTypes(paramsFilters, instance.filter.filterItems)},
      });
    } else {
      const limit = data.itemContent.length % productListLimitNumber || productListLimitNumber;
      dispatch({
        type: toAdd ? "ADD_ITEMS" : "SET_ITEMS",
        payload: {
          items: data.itemContent.slice(toAdd ? -limit : 0),
          total: data.totalSearchHits,
        },
      });
    }
  };

  const saveDataToSessionStorage = (data: IItemSearchResponse) => {
    const keyToSave = buildRequestString(state);
    const {offset} = state;
    if (offset && sessionStorage) {
      const keysToReplace = Object.keys(sessionStorage).filter((key) => key.startsWith(storageIdentifier));

      keysToReplace.forEach((keyToRemove) => {
        const requestParams = new URLSearchParams(keyToRemove.substring(storageIdentifier.length + 1));
        const limit = Number(requestParams.get("limit"));
        if (limit > ProductList.PRODUCT_LIST_DEFAULT_LIMIT * 2) {
          browserStorage.remove(keyToRemove);
        }
      });
    }
    browserStorage.write(keyToSave, data);
  };

  const preLoading = (firstResponse: IItemSearchResponse) => {
    const instance = state.instance;
    if (!instance) {
      return;
    }
    const paramsFilters = mapUrlParamsToFilterState(state).filters;
    if (Object.keys(paramsFilters).every((key) => !!state.filters[key])) {
      instance.offset = state.offset + ProductList.PRODUCT_LIST_DEFAULT_LIMIT;
      let requestPromise: Promise<IItemSearchResponse>;
      if (isSearchPage) {
        requestPromise = searchListFetch();
      } else {
        requestPromise = getItemsWithPercentDiscount(instance, state.filters);
      }
      void requestPromise.then((nextResponse) => {
        const nextResponseCopy = Object.assign({}, nextResponse);
        nextResponseCopy.itemContent = [...firstResponse.itemContent, ...nextResponseCopy.itemContent];
        nextResponseCopy.availableFilters = firstResponse.availableFilters;
        nextResponseCopy.totalSearchHits = firstResponse.totalSearchHits;
        saveDataToSessionStorage(nextResponseCopy);
      });
    }
  };

  const getDataByLimit = (limit: number): IItemSearchResponse | null => {
    const requestString = buildRequestString(state);
    const params = new URLSearchParams(requestString);
    params.delete("limit");
    const requestStringNoLimit = params.toString();
    const maxLimitKey =
      sessionStorage &&
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(storageIdentifier))
        .filter((key) => {
          const keyPart = key.substring(storageIdentifier.length + 1);
          const keyParams = new URLSearchParams(keyPart);
          keyParams.delete("limit");
          const KeyParamsNoLimit = keyParams.toString();

          return KeyParamsNoLimit === requestStringNoLimit;
        })
        .sort((key1, key2) => {
          const params1 = new URLSearchParams(key1);
          const params2 = new URLSearchParams(key2);

          return Number(params2.get("limit")) - Number(params1.get("limit"));
        })[0];

    if (!maxLimitKey) {
      return null;
    }

    const maxKeyParams = new URLSearchParams(maxLimitKey);
    const maxLimitStored = Number(maxKeyParams.get("limit"));
    let storageData: IItemSearchResponse | null = null;
    if (limit <= maxLimitStored) {
      const maxStorageData = browserStorage.read(maxLimitKey);
      if (maxStorageData) {
        storageData = maxStorageData;
        storageData.itemContent = storageData.itemContent.slice(0, limit);
      }
    }

    if (limit === maxLimitStored && storageData) {
      preLoading(storageData);
    }

    return storageData;
  };

  const prepareRequest = () => {
    if (!state.instance) {
      return;
    }

    const instance = state.instance;
    const isOffsetChanged = state.isOffsetChanged;
    if (isOffsetChanged) {
      state.isOffsetChanged = false;
    }

    instance.offset = state.offset;
    instance.limit = state.limit;
    instance.filter.applyFilterValue(state.filters);
    instance.orderBy = state.orderBy;
    if (state.orderBy === "innofind") {
      instance.userId = visitorId;
    } else {
      instance.userId = undefined;
    }
    instance.order = state.order;

    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    const storageData = getDataByLimit(instance.limit + instance.offset);

    if (storageData) {
      // When data locally available
      instance.filter.applyFilterResult(storageData.availableFilters);
      instance.items = storageData.itemContent;
      instance.total = storageData.totalSearchHits;
      handleResponse(storageData, isOffsetChanged);
      dispatch({
        type: "SET_LOADING",
        payload: false,
      });
      dispatch({type: "TRIGGER"});
    } else {
      let requestPromise: Promise<IItemSearchResponse>;
      if (isSearchPage) {
        requestPromise = searchListFetch();
      } else {
        // else get by api call
        requestPromise = getItemsWithPercentDiscount(instance, state.filters);
      }
      requestPromise
        .then((response) => {
          const firstResponseCopy = Object.assign({}, response);
          // offset means "mehr laden" was clicked
          if (instance.offset) {
            firstResponseCopy.itemContent = [...state.items, ...firstResponseCopy.itemContent];
          }
          handleResponse(response);
          saveDataToSessionStorage(firstResponseCopy);
          preLoading(firstResponseCopy);
        })
        .finally(() => {
          dispatch({
            type: "SET_LOADING",
            payload: false,
          });
        });
    }
  };

  // this effect will update the max and min values for range filters after every change of filters state
  // its done that way as work around because its very costly to do this in the backend (Pflaster Lösung)
  useEffect(() => {
    const instance = state.instance;
    if (!instance || isMobile) {
      return;
    }

    const availableRangeFiltersFields = Array.from(instance.filter.filterItems).reduce(
      (acc: string[], [key, keyValue]) => {
        if (
          keyValue.type === ProductListFilterItemType.range &&
          state.filters[key] !== undefined &&
          key !== "percentageDiscount"
        ) {
          acc.push(key);
        }

        return acc;
      },
      [],
    );

    // Whole thing only relevant, if there's more than one range filter set
    if (availableRangeFiltersFields.length < 2) {
      return;
    }
    availableRangeFiltersFields.forEach((key) => {
      const filtersNoKey = Object.assign({}, state.filters);
      delete filtersNoKey[key];

      const instanceCopy = Object.assign(Object.create(Object.getPrototypeOf(instance)), instance) as ProductList;

      instanceCopy.filter.applyFilterValue(filtersNoKey);
      instanceCopy.limit = 1;
      instanceCopy.offset = 0;

      void instanceCopy.send().then((data) => {
        const filterToDispatch = data.availableFilters.find((filter) => filter.field === key);
        if (filterToDispatch) {
          dispatch({
            type: "SET_RANGE_FILTERS_VALUES",
            payload: {
              field: filterToDispatch.field,
              value: {
                min: Math.floor(Number(filterToDispatch.minValue ?? 0)),
                max: Math.ceil(Number(filterToDispatch.maxValue ?? 0)),
              },
            },
          });
        }
      });
    });
  }, [JSON.stringify(state.filters), state.instance?.filter.filterItems.size]);

  useEffect(prepareRequest, [JSON.stringify(state.filters), state.orderBy, state.order, state.offset, state.limit]);

  return <ProductListContext.Provider value={{state, dispatch}}>{children}</ProductListContext.Provider>;
};

export default ProductListProvider;
